import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { DynamicSVG, Images } from '@lua-frontend/numu-ui';
import {
  FiltersContainer,
  FilterButton,
  FilterIconButton,
  FilterDropdownMenu,
  DropdownSection,
  SectionHeader,
  CheckboxItem
} from '../styles';
import { NotificationPanelContext } from '../index';

const HeaderFilters = () => {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const filterIconRef = useRef(null);
  const dropdownRef = useRef(null);
  const { list, filterOptions, dispatch, setFilterOptions } = useContext(NotificationPanelContext);

  const defaultFilters = [
    { label: 'All', value: 'all' },
    ...Object.keys(list.control).map((key) => ({
      label: list.control[key].filterLabel,
      value: key
    }))
  ];

  const filters = defaultFilters;

  // Sub-filter options mapped to filter values

  const subFilterOptions = {
    campaign: list.control.campaign.subTypes.map((subType) => subType.key),
    chat: list.control.chat.subTypes.map((subType) => subType.key),
    task: list.control.task.subTypes.map((subType) => subType.key),
    billing: list.control.billing.subTypes.map((subType) => subType.key),
    request: list.control.request?.subTypes?.map((subType) => subType.key) || []
  };

  // Map filter values to section keys
  const filterToSectionMap = {
    all: null, // Show all sections
    campaign: 'campaign',
    chat: 'chat',
    task: 'task',
    billing: 'billing',
    request: 'request'
  };

  // Get sections to display based on selected filter
  const getVisibleSections = () => {
    if (filterOptions.selectedType === 'all') {
      // Show all sections when "All" is selected
      return Object.entries(subFilterOptions).filter(([_, items]) => items.length > 0);
    }

    const sectionKey = filterToSectionMap[filterOptions.selectedType];
    if (!sectionKey || !subFilterOptions[sectionKey]) {
      return []; // No subtypes for this filter
    }

    return [[sectionKey, subFilterOptions[sectionKey]]];
  };

  // Check if any sub-filters are selected
  const hasSelectedSubFilters = Object.values(filterOptions.selectedSubTypes).some(
    (value) => value === true
  );

  // Function to calculate and update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (isFilterDropdownOpen && filterIconRef.current) {
      const rect = filterIconRef.current.getBoundingClientRect();
      // Position dropdown to the right of the button, aligned to the right edge
      setDropdownCoords({
        top: rect.bottom + 8, // Position below the button with small gap
        left: rect.right - 280 // Align dropdown right edge with button right edge (dropdown width is ~280px)
      });
    }
  }, [isFilterDropdownOpen]);

  // Update position when dropdown opens
  useEffect(() => {
    if (isFilterDropdownOpen) {
      updateDropdownPosition();
    }
  }, [isFilterDropdownOpen, updateDropdownPosition]);

  // Handle window resize and scroll events
  useEffect(() => {
    if (!isFilterDropdownOpen) return;

    let rafId = null;

    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    };

    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isFilterDropdownOpen, updateDropdownPosition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterIconRef.current &&
        !filterIconRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFilterDropdownOpen]);

  const handleFilterClick = (value) => {
    dispatch(setFilterOptions({ selectedType: value, selectedSubTypes: [] }));
  };

  const handleSubFilterToggle = (section, key) => {
    const newSelectedSubTypes = filterOptions.selectedSubTypes.includes(key)
      ? filterOptions.selectedSubTypes.filter((item) => item !== key)
      : [...filterOptions.selectedSubTypes, key];
    dispatch(setFilterOptions({ selectedSubTypes: newSelectedSubTypes }));
  };

  const visibleSections = getVisibleSections();

  return (
    <FiltersContainer>
      {filters.map((filter) => (
        <FilterButton
          key={filter.value}
          active={filterOptions.selectedType === filter.value}
          onClick={() => handleFilterClick(filter.value)}
        >
          {filter.label}
        </FilterButton>
      ))}
      <div style={{ position: 'relative' }}>
        <FilterIconButton
          ref={filterIconRef}
          active={hasSelectedSubFilters}
          onClick={(e) => {
            e.stopPropagation();
            setIsFilterDropdownOpen(!isFilterDropdownOpen);
          }}
          aria-label="Filter options"
        >
          <DynamicSVG
            svgPath={Images.notificationHeaderFilterSvg}
            style={{
              color: hasSelectedSubFilters ? '#0095FF' : '#0C0D0D',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </FilterIconButton>

        {isFilterDropdownOpen &&
          dropdownCoords &&
          visibleSections.length > 0 &&
          createPortal(
            <FilterDropdownMenu
              ref={dropdownRef}
              top={dropdownCoords.top}
              left={dropdownCoords.left}
            >
              {visibleSections.map(([section, items]) => (
                <DropdownSection key={section}>
                  <SectionHeader>{list.control[section]?.filterLabel || section}</SectionHeader>
                  {items.map((item) => {
                    const key = item;
                    return (
                      <CheckboxItem key={key}>
                        <input
                          type="checkbox"
                          checked={filterOptions.selectedSubTypes.includes(key)}
                          onChange={() => handleSubFilterToggle(section, item)}
                        />
                        <span>
                          {
                            list.control[section]?.subTypes?.find((subType) => subType.key === item)
                              ?.filterLabel
                          }
                        </span>
                      </CheckboxItem>
                    );
                  })}
                  {visibleSections.length > 1 &&
                    section !== visibleSections[visibleSections.length - 1][0] && (
                      <div
                        style={{ height: '1px', backgroundColor: '#E0E0E0', margin: '8px 0' }}
                      ></div>
                    )}
                </DropdownSection>
              ))}
            </FilterDropdownMenu>,
            document.body
          )}
      </div>
    </FiltersContainer>
  );
};

export default HeaderFilters;
